/**
 * IncidenciasFeed — listado unificado de incidencias o ampliaciones
 * para gestores/admin. Reusa el mismo componente para ambos tipos.
 *
 * Filtros: estado (open / overdue / closed / all), target (perito / self /
 * otro_gestor / all) y búsqueda libre.
 *
 * Acciones por fila:
 *   - Ir al expediente
 *   - Reclamar (solo si target=perito, abierta y sigues siendo gestor)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, ArrowRight, Clock, Warning, ListPlus, BellRinging, CheckCircle,
  Hourglass, User as UserIcon, Copy, CalendarPlus,
} from "@phosphor-icons/react";

const ESTADOS = [
  { k: "open",     label: "Abiertas" },
  { k: "overdue",  label: "Vencidas" },
  { k: "closed",   label: "Cerradas" },
  { k: "all",      label: "Todas" },
];

const TARGETS = [
  { k: "all",          label: "Todos" },
  { k: "perito",       label: "Perito" },
  { k: "self",         label: "Me la quedé" },
  { k: "otro_gestor",  label: "Otro gestor" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function dueLabel(iso) {
  if (!iso) return null;
  const now = Date.now();
  const due = new Date(iso).getTime();
  if (isNaN(due)) return null;
  const diff = due - now;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const txt = h >= 1 ? `${h}h ${m}m` : `${m}m`;
  return { overdue: diff < 0, label: diff < 0 ? `Vencida hace ${txt}` : `Vence en ${txt}` };
}

export default function IncidenciasFeed({ tipo, canSee, currentUser, emptyReason }) {
  const [estado, setEstado] = useState("open");
  const [target, setTarget] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const TitleIcon = tipo === "incidencia" ? Warning : ListPlus;
  const titulo = tipo === "incidencia" ? "Incidencias" : "Ampliaciones";

  const load = useCallback(async () => {
    if (!canSee) { setItems([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ tipo, estado, target });
      if (search.trim()) params.set("search", search.trim());
      const r = await api.get(`/gestores/incidencias?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [canSee, tipo, estado, target, search]);

  useEffect(() => { load(); }, [load]);

  const reclamar = async (exp_id, inc_id) => {
    const note = window.prompt("Nota (opcional) para reclamar al perito:") ?? null;
    if (note === null) return;   // cancel
    try {
      await api.post(`/expedientes/${exp_id}/incidencias/${inc_id}/reclamar`, { note: note || "" });
      toast.success("Reclamación registrada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reclamar");
    }
  };

  const counts = useMemo(() => {
    const c = { open: 0, overdue: 0, closed: 0 };
    for (const i of items) {
      if (i.item?.respuesta) c.closed += 1;
      else { c.open += 1; if (i.overdue) c.overdue += 1; }
    }
    return c;
  }, [items]);

  if (!canSee) {
    return (
      <div className="px-4 md:px-6 max-w-6xl mx-auto py-6">
        <div className="bg-white border border-border rounded-lg p-6 text-center">
          <TitleIcon className="w-6 h-6 text-muted mx-auto mb-2" weight="duotone" />
          <h2 className="font-display font-bold text-base">{titulo}</h2>
          <p className="text-[12.5px] text-muted mt-1">
            {emptyReason || "No tienes acceso a este panel."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 max-w-6xl mx-auto py-2" data-testid={`incidencias-feed-${tipo}`}>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 bg-white border border-border rounded-md p-0.5">
          {ESTADOS.map((e) => (
            <button
              key={e.k}
              onClick={() => setEstado(e.k)}
              data-testid={`incidencias-filter-estado-${e.k}`}
              className={`text-[11.5px] font-semibold px-2.5 py-1 rounded transition-colors ${
                estado === e.k ? "bg-foreground text-white" : "text-muted hover:text-foreground hover:bg-zinc-50"
              }`}
            >
              {e.label}
              {e.k === "overdue" && counts.overdue > 0 && (
                <span className="ml-1 text-[10px] bg-rose-600 text-white rounded-full px-1.5">{counts.overdue}</span>
              )}
            </button>
          ))}
        </div>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid="incidencias-filter-target"
          className="text-[11.5px] border border-border rounded-md h-7 px-2 bg-white"
        >
          {TARGETS.map((t) => <option key={t.k} value={t.k}>Destino: {t.label}</option>)}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar texto, expediente, perito..."
          data-testid="incidencias-filter-search"
          className="text-[12px] border border-border rounded-md h-7 px-2 bg-white flex-1 min-w-[180px]"
        />
        <button
          onClick={load}
          title="Refrescar"
          data-testid="incidencias-refresh"
          className="text-muted hover:text-foreground p-1"
        >
          <ArrowsClockwise className="w-4 h-4" weight="bold" />
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading && items.length === 0 && (
          <div className="py-8 text-center text-muted text-sm">Cargando…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="py-10 text-center text-muted text-sm italic">
            No hay {titulo.toLowerCase()} {estado !== "all" ? `en estado "${ESTADOS.find((x) => x.k === estado).label.toLowerCase()}"` : ""} ahora mismo.
          </div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-border" data-testid={`incidencias-list-${tipo}`}>
            {items.map((row) => (
              <IncidenciaRow
                key={row.item.id}
                row={row}
                tipo={tipo}
                onReclamar={reclamar}
                currentUser={currentUser}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function IncidenciaRow({ row, tipo, onReclamar, currentUser }) {
  const it = row.item;
  const due = dueLabel(it.due_at);
  const closed = !!it.respuesta;
  const target = it.target_type || "perito";

  const targetLabel =
    target === "self"
      ? `Yo (${it.target_user_name || it.target_user_code || ""})`
      : target === "otro_gestor"
      ? `Gestor: ${it.target_user_name || it.target_user_code || ""}`
      : `Perito${row.perito_codigo ? `: ${row.perito_codigo}` : " asignado"}`;

  const TargetIcon = target === "perito" ? UserIcon : CheckCircle;

  const isGestor = ["admin", "gestor_senior", "gestor_siniestros"].includes(currentUser?.role);
  const canReclamar = isGestor && !closed && target === "perito";

  return (
    <li
      data-testid={`incidencia-row-${it.id}`}
      className={`px-4 md:px-5 py-3 flex items-start gap-3 transition-colors ${
        row.overdue && !closed ? "bg-rose-50/60 hover:bg-rose-50" : "hover:bg-zinc-50"
      }`}
    >
      <div className={`w-9 h-9 rounded-md border flex items-center justify-center flex-shrink-0 ${
        tipo === "incidencia"
          ? "bg-rose-50 border-rose-200 text-rose-700"
          : "bg-amber-50 border-amber-200 text-amber-700"
      }`}>
        {tipo === "incidencia"
          ? <Warning className="w-4 h-4" weight="duotone" />
          : <ListPlus className="w-4 h-4" weight="duotone" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[13.5px] text-foreground">
            {row.numero_siniestro || "Expediente"}
          </span>
          {closed ? (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-emerald-50 text-emerald-800 border-emerald-200">
              Cerrada
            </span>
          ) : row.overdue ? (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-rose-100 text-rose-800 border-rose-200">
              Vencida
            </span>
          ) : (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-amber-50 text-amber-800 border-amber-200">
              Abierta
            </span>
          )}
          {it.resolved_by_self && (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-indigo-50 text-indigo-800 border-indigo-200">
              Resuelta por gestor
            </span>
          )}
          {!closed && (it.reclamaciones?.length || 0) > 0 && (
            <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-orange-50 text-orange-800 border-orange-200 inline-flex items-center gap-1">
              <BellRinging className="w-2.5 h-2.5" weight="bold" />
              {it.reclamaciones.length} reclamación{it.reclamaciones.length === 1 ? "" : "es"}
            </span>
          )}
          {it.source === "autoagenda" && (
            <span
              data-testid={`incidencia-autoagenda-badge-${it.id}`}
              className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border bg-sky-50 text-sky-800 border-sky-200 inline-flex items-center gap-1"
              title="Cita reservada por el asegurado vía MS Bookings"
            >
              <CalendarPlus className="w-2.5 h-2.5" weight="bold" />
              Autoagenda
            </span>
          )}
          {row.aseguradora && (
            <span className="text-[10.5px] text-muted">· {row.aseguradora}</span>
          )}
        </div>
        <div className="text-[12.5px] text-foreground/90 mt-0.5 line-clamp-2 break-words">
          {it.pregunta}
        </div>
        {closed && it.respuesta && (
          <div className="text-[11.5px] text-emerald-900 mt-1 bg-emerald-50/70 border border-emerald-100 rounded px-2 py-1 line-clamp-2">
            <span className="font-semibold">R.</span> {it.respuesta}
          </div>
        )}
        <div className="text-[10.5px] text-muted mt-1.5 inline-flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" weight="bold" />
            {fmtDate(it.created_at)}
          </span>
          {it.created_by_name && <span>· por {it.created_by_code || it.created_by_name}</span>}
          <span className="inline-flex items-center gap-1">
            <TargetIcon className="w-2.5 h-2.5" weight="bold" /> {targetLabel}
          </span>
          {!closed && due && (
            <span className={`inline-flex items-center gap-1 font-semibold ${
              due.overdue ? "text-rose-700" : "text-foreground/70"
            }`}>
              <Hourglass className="w-2.5 h-2.5" weight="bold" />
              {due.label} <span className="text-muted font-normal">(SLA {it.sla_hours}h)</span>
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {it.source === "autoagenda" && it.copy_text && (
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(it.copy_text);
                toast.success("Texto copiado · listo para pegar en la compañía");
              } catch {
                // Fallback: textarea oculto + execCommand para navegadores
                // antiguos / contextos no-HTTPS.
                const ta = document.createElement("textarea");
                ta.value = it.copy_text;
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand("copy"); toast.success("Texto copiado"); }
                catch { toast.error("No se pudo copiar"); }
                ta.remove();
              }
            }}
            data-testid={`incidencia-copy-${it.id}`}
            title={it.copy_text}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" weight="bold" /> Copiar para compañía
          </button>
        )}
        {canReclamar && (
          <button
            onClick={() => onReclamar(row.expediente_id, it.id)}
            data-testid={`incidencia-reclamar-${it.id}`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1"
          >
            <BellRinging className="w-3 h-3" weight="bold" /> Reclamar
          </button>
        )}
        <Link
          to={`/review/${row.expediente_id}`}
          data-testid={`incidencia-goto-${it.id}`}
          className="text-[11px] inline-flex items-center gap-1 text-brand hover:underline"
        >
          Ir al expediente <ArrowRight className="w-3 h-3" weight="bold" />
        </Link>
      </div>
    </li>
  );
}
